import {BlobUrlManager} from "@colibrio/colibrio-reader-framework/colibrio-core-io-base";
import {ITtsUtteranceData} from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import {ILocalizableString} from "@colibrio/colibrio-reader-framework/colibrio-core-base";
import {HashUtils} from "../../HashUtils";
import {IElevenLabsApiClient} from "./types/IElevenLabsApiClient";
import {IElevenLabsTextToSpeechRequestData} from "./types/IElevenLabsTextToSpeechRequestData";
import {ElevenLabsOutputFormats} from "./types/ElevenLabsOutputFormats";
import {IElevenLabsVoiceSettingsData} from "./types/IElevenLabsVoiceSettingsData";

/**
 * ElevenLabsUtterance
 *
 * This class is a step to normalize the Eleven Labs to other TTS APIs by adding an "Utterance" concept.
 * An Utterance traditionally represents the text plus various options that is sent to a TTS speech synthesizer.
 *
 * */
export class ElevenLabsUtterance {
    private _arrayBuffer: ArrayBuffer | undefined;
    private _blobUrl: string | undefined;
    private _blobUrlManager = new BlobUrlManager();
    private _hash: string | undefined;
    private _elevenLabsTtsRequestData: IElevenLabsTextToSpeechRequestData;

    constructor(private _voiceId: string,
                private _utteranceData: ITtsUtteranceData,
                outputFormat: ElevenLabsOutputFormats,
                voiceSettings: IElevenLabsVoiceSettingsData,
                private _apiClient: IElevenLabsApiClient) {
        this._elevenLabsTtsRequestData = {
            voiceId: this._voiceId,
            text: this._utteranceData.text.value,
            outputFormat,
            voiceSettings,
        }
    }

    get id(): number {
        return this._utteranceData.id;
    }

    get text(): ILocalizableString {
        return this._utteranceData.text;
    }

    async fetchHashSignature(): Promise<string> {
        if (this._hash) {
            return this._hash;
        }

        this._hash = await HashUtils.generateSHA256Hash(`${JSON.stringify(this._elevenLabsTtsRequestData)}`);
        return this._hash;
    }

    get speechData(): ArrayBuffer | undefined {
        return this._arrayBuffer;
    }

    get hasSpeechData(): boolean {
        return this._arrayBuffer !== undefined;
    }

    async fetchSpeechData(): Promise<void> {
        if (this._arrayBuffer) {
            return;
        }
        this._arrayBuffer = await this._apiClient.textToSpeech(this._elevenLabsTtsRequestData);
    }

    /*
    * Transforms the objects ArrayBuffer into a temporary BlobUrl for use by the `ElevenLabsUtteranceRenderer`.
    * */
    getBlobUrl(): string | undefined {
        if (!this._arrayBuffer) {
            console.warn('ElevenLabsUtterance.getBlobUrl(): This utterance has no speech data at this point.');
            return;
        }
        return this._blobUrl ? this._blobUrl : this._bufferToBlobUrl(this._arrayBuffer);
    }

    revokeBlobUrl(): void {
        if (!this._blobUrl) {
            console.warn('ElevenLabsUtterance.revokeBlobUrl(): This utterance has no blob url at this point.');
            return;
        }
        this._blobUrlManager.revokeUrl(this._blobUrl);
    }

    clearSpeechData(): void {
        this.revokeBlobUrl();
        this._arrayBuffer = undefined;
    }

    private _bufferToBlobUrl(buffer: ArrayBuffer): string {
        return this._blobUrlManager.createUrl([buffer], "audio/mpeg", false).url;
    }

}
